/**
 * Builds Datadog deep links for a single inbound SMS, so the team can jump
 * straight to that message's backend logs and LLM traces instead of
 * hand-searching in Datadog.
 */

const DATADOG_ORIGIN = 'https://app.datadoghq.com';

/** Datadog time params are absolute epoch ms, so the viewer's timezone never matters. */
const WINDOW_MS = 2 * 60 * 1000;

/** Logs and LLM traces are only retained for 15 days; older messages get no links. */
const RETENTION_DAYS = 15;
const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

/** Health-check noise that would otherwise drown out the phone number match. */
const LOG_NOISE_EXCLUSIONS = ['-"GET /healthz"', '-"GET / HTTP"'];

const TRACES_SERVICE_SUFFIX = '-conversation-context-service';

export interface DatadogLinks {
  logsUrl: string;
  llmTracesUrl: string;
}

/** Digits only — the traces search matches the phone with or without the `+1`. */
const digitsOnly = (phone: string) => phone.replace(/\D/g, '');

/** `+1` form, matching how the phone appears in the backend log lines. */
const withPlus = (phone: string) => (phone.startsWith('+') ? phone : `+${digitsOnly(phone)}`);

/**
 * `message.time` is an `any`-typed custom scalar, so anything unparseable
 * (null, empty string, a malformed date) has to fall out here rather than
 * producing a `NaN` time range.
 */
const toEpochMs = (time: unknown): number | null => {
  if (time == null || typeof time === 'boolean') return null;
  const ms = new Date(time as string | number | Date).getTime();
  return Number.isFinite(ms) ? ms : null;
};

export const isWithinDatadogRetention = (timeMs: number, now: number = Date.now()) =>
  now - timeMs <= RETENTION_MS;

/**
 * URLSearchParams encodes spaces as `+`; Datadog's own URLs use `%20`. Safe to
 * swap globally because a literal `+` (the phone prefix) is already `%2B` by
 * this point, so every remaining bare `+` is a space.
 */
const toQueryString = (params: Record<string, string>) =>
  new URLSearchParams(params).toString().replace(/\+/g, '%20');

/**
 * Returns null when a link would be useless: no phone, an unparseable
 * timestamp, a message past Datadog's retention window, or a build with no
 * `VITE_DATADOG_ENV` (which would otherwise query `env:undefined`).
 */
export const buildDatadogLinks = (
  phone: string | null | undefined,
  time: unknown,
  env: string | undefined = import.meta.env.VITE_DATADOG_ENV,
  now: number = Date.now(),
): DatadogLinks | null => {
  if (!phone || !env) return null;

  const timeMs = toEpochMs(time);
  if (timeMs === null || !isWithinDatadogRetention(timeMs, now)) return null;

  const from = timeMs - WINDOW_MS;
  const to = timeMs + WINDOW_MS;

  const logsQuery = [`env:${env}`, ...LOG_NOISE_EXCLUSIONS, `"${withPlus(phone)}"`].join(' ');

  const logsParams = toQueryString({
    query: logsQuery,
    agg_m: 'count',
    agg_m_source: 'base',
    agg_t: 'count',
    cols: 'host,service',
    messageDisplay: 'inline',
    refresh_mode: 'paused',
    storage: 'hot',
    stream_sort: 'desc',
    viz: 'stream',
    from_ts: String(from),
    to_ts: String(to),
    live: 'false',
  });

  const tracesQuery = [
    `@ml_app:${env}${TRACES_SERVICE_SUFFIX}`,
    '@event_type:span',
    '@is_root_span:true',
    `"${digitsOnly(phone)}"`,
  ].join(' ');

  const tracesParams = toQueryString({
    query: tracesQuery,
    agg_m: 'count',
    agg_m_source: 'base',
    agg_t: 'count',
    selectedTab: 'overview',
    start: String(from),
    end: String(to),
    paused: 'true',
  });

  return {
    logsUrl: `${DATADOG_ORIGIN}/logs?${logsParams}`,
    llmTracesUrl: `${DATADOG_ORIGIN}/llm/traces?${tracesParams}`,
  };
};
